import { useEffect, useRef, useState } from 'react';
import { view } from '@yola/ws-sdk';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import getBreakpointForCurrentDevice from '../../../../breakpoint-editing/helpers/get-breakpoint-for-current-device';
import { DESKTOP_BREAKPOINT } from '../../../../breakpoint-editing/constants/breakpoints';
import positionProperties from '../../../common/constants/position-properties';
import getSuitableTransformPosition from '../../../common/helpers/get-suitable-transform-position';
import getPositionValues from '../../../common/helpers/get-position-values';
import setPositionCustomProperties from '../../../common/helpers/set-position-custom-properties';
import usePositioningArea from '../../../common/hooks/use-positioning-area';
import useDrag from '../../../common/hooks/use-drag';
import computePositionCorrection from '../helpers/compute-position-correction';
import useAxisLocker from './use-axis-locker';

const DEFAULT_POSITION_X = 0;
const DEFAULT_POSITION_Y = 0;
const POSITION_X_MIN = 0;
const POSITION_X_MAX = 100;
const POSITION_Y_MIN = 0;
const POSITION_Y_MAX = 100;

const convertPxToPercentages = (value, length) => (value * 100) / length;
const parsePositionValue = (value) => {
  const parsedValue = parseFloat(value);

  if (value.includes('%')) {
    return parsedValue;
  }

  return null;
};

const defaultElementsToStick = [];

const useDragAndPositioning = ({
  handleRef,
  elementId,
  scrollPosition,
  onStart,
  onMove,
  onEnd,
  elementsToStick = defaultElementsToStick,
  stickShiftSize = 5,
  shiftPressed = false,
}) => {
  const element = view.accessors.getLiveElement(elementId);
  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;
  const activeBreakpoint = isBreakpointEditingEnabled
    ? getBreakpointForCurrentDevice()
    : DESKTOP_BREAKPOINT;
  const [initialElementBounds, setInitialElementBounds] = useState(() =>
    element.getBoundingClientRect()
  );

  const isDraggingRef = useRef(false);
  const lastRawDistanceRef = useRef([0, 0]);
  const lastDragDistanceRef = useRef(null);
  const { computeDistanceWithLock } = useAxisLocker({ locked: shiftPressed });

  const { positioningAreaBounds, positioningAreaClipPath, updateClipPath } = usePositioningArea(
    element,
    scrollPosition
  );

  const [position, setPosition] = useState(() => {
    const { x: inlineX, y: inlineY } = getPositionValues(element, activeBreakpoint);

    if (inlineX !== null && inlineY !== null) {
      return {
        x: inlineX,
        y: inlineY,
      };
    }

    const computedStyle = window.getComputedStyle(element);
    const x =
      parsePositionValue(computedStyle.getPropertyValue(positionProperties.X)) ||
      DEFAULT_POSITION_X;
    const y =
      parsePositionValue(computedStyle.getPropertyValue(positionProperties.Y)) ||
      DEFAULT_POSITION_Y;

    return {
      x,
      y,
    };
  });
  const [initialDragPosition, setInitialDragPosition] = useState({});

  const getSuitablePosition = ({ axisDistance, axisProp, startPosition, elementBounds }) => {
    const distanceSign = Math.sign(axisDistance);
    const absDistance = Math.abs(axisDistance);

    return (
      getSuitableTransformPosition({
        areaBounds: positioningAreaBounds,
        distance: absDistance,
        elementBounds,
        axisProp,
        startPosition,
      }) * distanceSign
    );
  };

  const updatePosition = (xDistance, yDistance) => {
    const elementBounds = element.getBoundingClientRect();
    const isXPositioningLocked = elementBounds.width > positioningAreaBounds.width;
    const isYPositioningLocked = elementBounds.height > positioningAreaBounds.height;

    if (isXPositioningLocked && isYPositioningLocked) {
      return;
    }

    let { x, y } = position;
    if (!isXPositioningLocked) {
      const xDistanceInPercentages = getSuitablePosition({
        axisDistance: xDistance,
        axisProp: 'width',
        startPosition: convertPxToPercentages(xDistance, positioningAreaBounds.width),
        elementBounds,
      });

      x = Math.min(
        Math.max(initialDragPosition.x + xDistanceInPercentages, POSITION_X_MIN),
        POSITION_X_MAX
      );
    }

    if (!isYPositioningLocked) {
      const yDistanceInPercentages = getSuitablePosition({
        axisDistance: yDistance,
        axisProp: 'height',
        startPosition: convertPxToPercentages(yDistance, positioningAreaBounds.height),
        elementBounds,
      });

      y = Math.min(
        Math.max(initialDragPosition.y + yDistanceInPercentages, POSITION_Y_MIN),
        POSITION_Y_MAX
      );
    }

    setPositionCustomProperties(element, { x, y });
    setPosition({
      x,
      y,
    });
  };

  // Process movement with axis locking integration
  const processMovement = (distance) => {
    const { distance: distanceWithAxisLock, lockedAxis } = computeDistanceWithLock(distance);

    const correction = computePositionCorrection({
      initialElementBounds,
      positioningAreaBounds,
      distance: distanceWithAxisLock,
      elements: elementsToStick,
      correctionDistance: stickShiftSize,
      lockedAxis,
    });

    const correctedDistance = [
      distanceWithAxisLock[0] + correction.x,
      distanceWithAxisLock[1] + correction.y,
    ];

    // Skip if no effective change
    if (
      lastDragDistanceRef.current &&
      lastDragDistanceRef.current[0] === correctedDistance[0] &&
      lastDragDistanceRef.current[1] === correctedDistance[1]
    ) {
      return;
    }

    lastDragDistanceRef.current = correctedDistance;
    updatePosition(correctedDistance[0], correctedDistance[1]);
    updateClipPath();
    onMove(correctedDistance, { correction });
  };

  const processMovementRef = useRef(processMovement);

  useEffect(() => {
    processMovementRef.current = processMovement;
  });

  useDrag(
    [handleRef],
    {
      onStart() {
        setInitialDragPosition({ x: position.x, y: position.y });
        updateClipPath();
        onStart();
        setInitialElementBounds(element.getBoundingClientRect());
        isDraggingRef.current = true;
        lastRawDistanceRef.current = [0, 0];
      },

      onMove(distance) {
        // Cache the latest raw distance from drag start for reactive recalculation
        lastRawDistanceRef.current = distance;

        if (!distance[0] && !distance[1]) {
          return;
        }

        processMovement(distance, shiftPressed);
      },

      onEnd() {
        lastDragDistanceRef.current = null;
        isDraggingRef.current = false;
        lastRawDistanceRef.current = [0, 0];
        onEnd();
      },
    },
    { throttleMove: true }
  );

  useEffect(() => {
    if (!isDraggingRef.current) {
      return;
    }

    const raw = lastRawDistanceRef.current || [0, 0];

    processMovementRef.current(raw);
    // we need immediate recalculation on Shift toggle
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [shiftPressed]);

  return {
    positioningAreaBounds,
    positioningAreaClipPath,
  };
};

export default useDragAndPositioning;
